import { TypedDocumentNode, gql } from '@apollo/client';
import { lazy } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineM, LabelM } from '@sorare/core/src/atoms/typography';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { StuffInRewardBoxType } from '@sorare/core/src/components/rewards/RewardBoxDialog/types';
import { RewardCount } from '@sorare/core/src/components/rewards/RewardCount';
import { DeliverableItemOrderFooter } from '@sorare/core/src/components/rewards/deliverableItem/DeliverableItemOrderFooter';
import { isType } from '@sorare/core/src/gql';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useGenerateDialogPath } from '@sorare/core/src/hooks/navigation/useGenerateDialogPath';

import { deliverableItemOrderFragment } from 'components/rewards/deliverableItem/DeliverableItemOrderFormDialog';

import {
  DeliverableItemInRewardBox_CurrentUser,
  DeliverableItemInRewardBox_ProbabilisticBundleSlotItemInterface,
  DeliverableItemInRewardBox_ProbabilisticBundleSlotItemInterface_ProbabilisticBundleSlotDeliverableItem_,
  UnclaimedDeliveryItemOrders,
  UnclaimedDeliveryItemOrdersVariables,
} from './__generated__/index.graphql';

const JerseyInRewardBox = lazy(
  async () => import('@sorare/trois/src/components/RewardBox/JerseyInRewardBox')
);

const DescriptionRoot = styled(Vertical)`
  width: max-content;
  white-space: normal;
`;

const currentUserFragment = gql`
  fragment DeliverableItemInRewardBox_CurrentUser on CurrentUser {
    slug
    id
    unclaimedDeliverableItemOrders(deliverableItemId: $deliverableItemId) {
      id
      ...DeliverableItemOrderFormDialog_deliverableItemOrderFragment
    }
  }
  ${deliverableItemOrderFragment}
` as TypedDocumentNode<DeliverableItemInRewardBox_CurrentUser>;

const UNCLAIMED_DELIVERY_ITEM_ORDERS = gql`
  query UnclaimedDeliveryItemOrders($deliverableItemId: ID!) {
    currentUser {
      slug
      ...DeliverableItemInRewardBox_CurrentUser
    }
  }
  ${currentUserFragment}
` as TypedDocumentNode<
  UnclaimedDeliveryItemOrders,
  UnclaimedDeliveryItemOrdersVariables
>;

type Props = {
  item: DeliverableItemInRewardBox_ProbabilisticBundleSlotItemInterface;
  visible: boolean;
};

const DeliverableItemInRewardBoxCanHandle = (
  item: Props['item']
): item is DeliverableItemInRewardBox_ProbabilisticBundleSlotItemInterface_ProbabilisticBundleSlotDeliverableItem_ => {
  return isType(item, 'ProbabilisticBundleSlotDeliverableItem');
};

export const DeliverableItemInRewardBox: StuffInRewardBoxType<
  DeliverableItemInRewardBox_ProbabilisticBundleSlotItemInterface
> = ({ item, visible }: Props) => {
  const navigate = useNavigate();
  const generateDialogPath = useGenerateDialogPath();

  if (!DeliverableItemInRewardBoxCanHandle(item)) {
    return null;
  }

  const { rewardDeliverableItem } = item;
  const { deliverableItem } = rewardDeliverableItem || {};

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data } = useQuery(UNCLAIMED_DELIVERY_ITEM_ORDERS, {
    variables: {
      deliverableItemId: deliverableItem!.id,
    },
    skip: !deliverableItem?.id,
    fetchPolicy: 'cache-first',
  });

  if (!isType(deliverableItem, 'JerseyDeliverableItem')) {
    return null;
  }

  const deliverableItemOrder =
    data?.currentUser?.unclaimedDeliverableItemOrders[0];

  return (
    <JerseyInRewardBox
      visible={visible}
      teamLogoUrl={deliverableItem.team?.pictureUrl || undefined}
      colors={
        deliverableItem.team?.styleGuideColor
          ? [deliverableItem.team?.styleGuideColor]
          : undefined
      }
      signed={deliverableItem.signed}
      description={
        <DescriptionRoot gap={2}>
          <HeadlineM as="span">
            <RewardCount
              quantity={rewardDeliverableItem?.quantity}
              rewardName={deliverableItem.name}
            />
          </HeadlineM>
          {deliverableItemOrder && (
            <DeliverableItemOrderFooter
              onClick={() => {
                navigate(
                  generateDialogPath(
                    DialogKey.deliveryItemOrder,
                    deliverableItemOrder.id
                  )
                );
              }}
            >
              <LabelM as="span">
                <FormattedMessage
                  id="DeliverableItemFooter.timeLimit"
                  defaultMessage="or you can do it within the next 15 days"
                />
              </LabelM>
            </DeliverableItemOrderFooter>
          )}
        </DescriptionRoot>
      }
    />
  );
};

DeliverableItemInRewardBox.fragments = {
  ProbabilisticBundleSlotItemInterface: gql`
    fragment DeliverableItemInRewardBox_ProbabilisticBundleSlotItemInterface on ProbabilisticBundleSlotItemInterface {
      id
      ... on ProbabilisticBundleSlotDeliverableItem {
        id
        rewardDeliverableItem {
          quantity
          deliverableItem {
            id
            slug
            name
            mandatoryWearableSizeInput
            ... on JerseyDeliverableItem {
              slug
              id
              signed
              team {
                ... on TeamInterface {
                  slug
                  pictureUrl
                  styleGuideColor
                }
              }
            }
          }
        }
      }
    }
  ` as TypedDocumentNode<DeliverableItemInRewardBox_ProbabilisticBundleSlotItemInterface>,
  // probabilisticBundlesOpenPayload: gql`
  //   fragment DeliverableItemInRewardBox_probabilisticBundlesOpenPayload on probabilisticBundlesOpenPayload {
  //     currentUser {
  //       slug
  //       ...DeliverableItemInRewardBox_CurrentUser
  //     }
  //   }
  //   ${currentUserFragment}
  // ` as TypedDocumentNode<DeliverableItemInRewardBox_probabilisticBundlesOpenPayload>,
};

const DeliverableItemInRewardBoxPreloader = () => {
  return null;
};

DeliverableItemInRewardBox.preloader = DeliverableItemInRewardBoxPreloader;

DeliverableItemInRewardBox.canHandle = DeliverableItemInRewardBoxCanHandle;

DeliverableItemInRewardBox.description = (item: Props['item']) => {
  if (!DeliverableItemInRewardBoxCanHandle(item)) {
    return null;
  }

  if (!item.rewardDeliverableItem) {
    return '';
  }

  const {
    quantity,
    deliverableItem: { name },
  } = item.rewardDeliverableItem;

  if (quantity > 1) {
    return `${quantity}× ${name}`;
  }
  return name;
};

DeliverableItemInRewardBox.fallback = (item: Props['item']) => {
  if (!DeliverableItemInRewardBoxCanHandle(item)) {
    return null;
  }
  return <div>{DeliverableItemInRewardBox.description(item)}</div>;
};
